import _cloneDeep from 'lodash/cloneDeep'
import _get from 'lodash/get'
import _orderBy from 'lodash/orderBy'
import _set from 'lodash/set'
import core from 'frontcore/index.js'
import useExperimentalFeaturesStore from 'frontcore/data/application-configuration/useExperimentalFeaturesStore'
import useLicenceExpirationConfiguration from 'frontcore/store/configurations/useLicenceExpirationConfiguration'
import { CONFIGURATION_KEYS } from 'frontcore/constants/constants'
import {
  createConfigurationKey,
  getConfiguration,
  getConfigurationByKeyName,
  getConfigurationKeys,
  updateConfigurationKey,
} from 'frontend/communications/apiClient'
import { PRODUCTION_HIERARCHY_SETTINGS_SUFFIX } from 'frontend/constants/settings'
import { useCustomerConfiguration } from 'frontend/presentation/configuration/store/useCustomerConfiguration'
import { useDeviceConfigurationStore } from 'frontcore/store/configurations/useDeviceConfigurationStore'
import { useProductionHierarchyStore } from 'frontcore/store/production-hierarchy/useProductionHierarchyStore'

const stateObj = {
  ANALYSIS_TOOLS_CONFIGURATION: [],
  CHART_VISIBILITY: {},
  CHARTS_CONFIG: {},
  CONFIGURATION_KEYS: [],
  CUSTOMER_INFO: true,
  DAILY_ROUND_CHECKPOINTS_CONFIG: {},
  DIAGNOSIS_CONFIGURATION: {},
  ENABLE_CUSTOM_ACTION_PLAN_SOURCES: true,
  ENABLE_DAILY_ROUND_WEEKLY_MEETING: false,
  ENABLE_PMM_INTEGRATION: false,
  EVOLUTION_CHARTS: {},
  FACTORY_TIME_ZONE: null,
  GATHER_LDAP_USERS: false,
  GATHER_SAP_USERS: false,
  GEMBA_WALK_CHECKPOINTS_CONFIG: {},
  HAS_CUSTOM_LOGIN_BACKGROUND: false,
  HSE_CONFIGURATION: {},
  INCLUDE_ACTIONS: true,
  INCLUDE_CUSTOMER_PROTECTION_FIELDS: [],
  INCLUDE_DIAGNOSIS: false,
  INCLUDE_EVIDENCE_TYPE: true,
  INCLUDE_FIVES_CALENDAR: true,
  INCLUDE_FIVES_LIST: true,
  INCLUDE_FIVES_PLAN: true,
  INCLUDE_FIVES: true,
  INCLUDE_PROBLEM_SOLVING: true,
  IS_VT_AUTONOMOUS: false,
  LOGIN_OPTIONS: {},
  LOGIN_TYPE: 0,
  LPA_CONFIGURATION: {},
  MASTER_AUDIT_ASSESSMENTS: {},
  OK_FIRST_PART: {},
  ORDER_COLUMNS_ROWS: 0,
  OVERVIEW_WIDGETS: [],
  PERMISSIONS_MATRIX: {},
  PROBLEMS_CONFIGURATION: [],
  SCORE_OPTIONS: [],
  SHOW_FIVES_FAMILY_CHART: false,
  SKILL_MATRIX: {},
  STANDARD_CONFIGURATION: {},
  TASK_MANAGER: {},
  THRESHOLDS: {},
  VARIABLE_NUMBER_OF_RESULTS_CHARTS: {},
  VERSION_MPS_NOW: null,
  VERSION: '',
  PASSWORD_REGEX: '',
  MAX_AUTHORIZATION_TOKENS: 0,
  [CONFIGURATION_KEYS.SWR_CONFIGURATION]: {},
  [CONFIGURATION_KEYS.IS_CAPABILITY_TO_REACH_SWT_ENABLED]: true,
  HAS_BRANDING_LOGO: false,
}

const gettersObj = {
  customerInfo (state) {
    return state.CUSTOMER_INFO
  },
  getAnalysisToolsConfigurations (state) {
    if (state.ANALYSIS_TOOLS_CONFIGURATION) {
      return state.ANALYSIS_TOOLS_CONFIGURATION
    }
    return []
  },
  getAnalysisToolsSources (state) {
    const sources = []
    Object.keys(state.ANALYSIS_TOOLS_CONFIGURATION).forEach((key) => {
      const value = state.ANALYSIS_TOOLS_CONFIGURATION[key]
      if (value.enabled && value.source) {
        sources.push(value.source)
      }
    })
    return sources
  },
  getLPAThresholds (state) {
    return state.LPA_CONFIGURATION.RESULT_THRESHOLDS
  },
  getProblemMainDateField (state) {
    return state.PROBLEMS_CONFIGURATION.PROBLEM_MAIN_DATE_FIELD
  },
  getPlanEfficiencyType (state, getters) {
    return getters.getConfigurationKeyByName(CONFIGURATION_KEYS.EFFICIENCY_PLAN_CONFIG).value.type
  },
  getPlanEfficiencyAssessmentsNumber (state, getters) {
    return getters.getConfigurationKeyByName(CONFIGURATION_KEYS.EFFICIENCY_PLAN_CONFIG).value.assessments_number
  },
  getIsCorporate (state) {
    return state.DIAGNOSIS_CONFIGURATION.IS_CORPORATE
  },
  hasCustomLoginBackground (state) {
    return state.HAS_CUSTOM_LOGIN_BACKGROUND
  },
  hasBrandingLogo (state) {
    return state.HAS_BRANDING_LOGO
  },
  isIndicatorsCoachingActive (state) {
    return state.PROBLEMS_CONFIGURATION.INDICATORS_COACHING
  },
  hasManyImmediateActionTypes (state) {
    if (!state.PROBLEMS_CONFIGURATION.ENABLE_IMMEDIATE_ACTION_TYPES) { return false }
    return state.PROBLEMS_CONFIGURATION.ENABLE_IMMEDIATE_ACTION_TYPES
  },
  isAnalysisToolEnabled: (state) => (tool) => {
    return state.ANALYSIS_TOOLS_CONFIGURATION?.[tool]?.enabled ?? false
  },
  isProblemShortLoopAvailable (state) {
    if (!state.PROBLEMS_CONFIGURATION.AVAILABLE_PROBLEM_LEVELS) { return false }
    return state.PROBLEMS_CONFIGURATION.AVAILABLE_PROBLEM_LEVELS.SHORT_LOOP ? state.PROBLEMS_CONFIGURATION.AVAILABLE_PROBLEM_LEVELS.SHORT_LOOP : false
  },
  isProblemJustDoitAvailable (state) {
    if (!state.PROBLEMS_CONFIGURATION.AVAILABLE_PROBLEM_LEVELS) { return false }
    return state.PROBLEMS_CONFIGURATION.AVAILABLE_PROBLEM_LEVELS.JUST_DO_IT ? state.PROBLEMS_CONFIGURATION.AVAILABLE_PROBLEM_LEVELS.JUST_DO_IT : false
  },
  isHSERiskModuleEnabled (state) {
    return state.HSE_CONFIGURATION?.ENABLED ?? false
  },
  isMultipleProblemLevels (state) {
    let numLevels = 0
    if (state.PROBLEMS_CONFIGURATION.AVAILABLE_PROBLEM_LEVELS) {
      for (const level in state.PROBLEMS_CONFIGURATION.AVAILABLE_PROBLEM_LEVELS) {
        if (state.PROBLEMS_CONFIGURATION.AVAILABLE_PROBLEM_LEVELS[level]) {
          numLevels = numLevels + 1
        }
      }
    }
    return numLevels > 1
  },
  isEfficiencyPlanActive (state, getters) {
    return getters.getConfigurationKeyByName(CONFIGURATION_KEYS.EFFICIENCY_PLAN_CONFIG).value.enabled
  },
  isProblemSupervisorEnabled (state, getters) {
    return getters.getConfigurationKeyByName(CONFIGURATION_KEYS.ENABLE_PROBLEM_SUPERVISOR).value
  },
  isIdentificationDefectsActive (state) {
    return state.PROBLEMS_CONFIGURATION.PROBLEM_DEFECTS === true || state.PROBLEMS_CONFIGURATION.PROBLEM_DEFECTS === 'true'
  },
  isRootProblemTop (state) {
    return state.PROBLEMS_CONFIGURATION.ROOT_CAUSE_POSITION_TOP
  },
  isShowFivewhysReminderEnabled (state) {
    return state.PROBLEMS_CONFIGURATION.SHOW_FIVEWHYS_REMINDER
  },
  isRootCauseShown (state) {
    return state.PROBLEMS_CONFIGURATION.SHOW_PROBLEM_ROOT_CAUSE
  },
  isVTAutonomous (state) {
    return state.IS_VT_AUTONOMOUS
  },
  isWeeklyMeetingEnabled (state) {
    return state.ENABLE_DAILY_ROUND_WEEKLY_MEETING
  },
  isPMMIntegrationEnabled (state) {
    return state.ENABLE_PMM_INTEGRATION
  },
  getLoginType (state) {
    return state.LOGIN_TYPE
  },
  getLoginOptions (state) {
    return state.LOGIN_OPTIONS
  },
  getOrderColumsRows (state) {
    return state.ORDER_COLUMNS_ROWS
  },
  getFactoryTimezone (state) {
    return state.FACTORY_TIME_ZONE
  },
  getLDAPUsers (state) {
    return state.GATHER_LDAP_USERS
  },
  getProblemsSignature (state) {
    return state.PROBLEMS_CONFIGURATION.ENABLE_PROBLEMS_SIGNATURE
  },
  getOverviewWidgets (state) {
    return state.OVERVIEW_WIDGETS
  },
  getConfigurationKeys (state) {
    return state.CONFIGURATION_KEYS
  },
  getConfigurationKeyByName: (state) => (name) => {
    return state.CONFIGURATION_KEYS.find(key => key.name === name)
  },
  getConfigurationValueByNameAndPath: (state, getters) => (configName, path) => {
    const fullValueObject = getters.getConfigurationKeyByName(configName).value
    return _get(fullValueObject, path)
  },
  getCustomTableFields: (state, getters) => (tableConfigKey, tableFields) => {
    const tableColumnsConfigurationKey = getters.getConfigurationKeyByName(CONFIGURATION_KEYS.TABLE_COLUMNS)
    if (!tableColumnsConfigurationKey) return []
    const fieldsSelected = tableConfigKey in tableColumnsConfigurationKey.value ? tableColumnsConfigurationKey.value[tableConfigKey] : []
    return tableFields.filter((field) => fieldsSelected.includes(field.slug)).sort((a, b) => {
      return fieldsSelected.indexOf(a.slug) - fieldsSelected.indexOf(b.slug)
    })
  },
  getProblemActionPlanTypes (state) {
    return state.PROBLEMS_CONFIGURATION.ACTION_PLAN_TYPES
  },
  getSAPUsers (state) {
    return state.GATHER_SAP_USERS
  },
  getThresholdByKey: (state) => (key) => {
    return state.THRESHOLDS[key]
  },
  get: (state) => (key) => {
    return state.CHART_VISIBILITY[key] ?? false
  },
  getChartVisibilityByKey: (state) => (key) => {
    return state.CHART_VISIBILITY[key] ?? false
  },
  getEvolutionChartConfigByKey: (state) => (key) => {
    return state.EVOLUTION_CHARTS[key] ?? null
  },
  getVariableNumberOfResultsChartsConfigByKey: (state) => (key) => {
    return state.VARIABLE_NUMBER_OF_RESULTS_CHARTS[key] ?? null
  },
  getChartConfigByKey: (state) => (key) => {
    return state.CHARTS_CONFIG[key] ?? null
  },
  getVersion (state) {
    return state.VERSION
  },
  getPasswordRegex (state) {
    return state.PASSWORD_REGEX
  },
  getMaxAuthorizationTokens (state) {
    return state.MAX_AUTHORIZATION_TOKENS
  },
  hasActions (state) {
    return state.INCLUDE_ACTIONS
  },
  hasAlerts (state) {
    const leanCenterTabsAertsConfiguration = state.CONFIGURATION_KEYS.find(k => k.name == 'LEAN_CENTER.LEAN_CENTER_TABS.ALERTS')
    return leanCenterTabsAertsConfiguration ? leanCenterTabsAertsConfiguration.value : false
  },
  hasProblemSolving (state) {
    return state.INCLUDE_PROBLEM_SOLVING
  },
  hasCustomerProtectionFields (state) {
    return state.INCLUDE_CUSTOMER_PROTECTION_FIELDS
  },
  has5WHYSFacts (state) {
    return '5WHYS' in state.ANALYSIS_TOOLS_CONFIGURATION && state.ANALYSIS_TOOLS_CONFIGURATION['5WHYS']['facts_enabled']
  },
  hasProblemReport (state) {
    return state.PROBLEMS_CONFIGURATION.PROBLEM_REPORT ? state.PROBLEMS_CONFIGURATION.PROBLEM_REPORT : false
  },
  hasTransversalizationTool (state) {
    return state.PROBLEMS_CONFIGURATION.TRANSVERSALIZATION_TOOL
  },
  hasCustomerProtection (state) {
    return state.PROBLEMS_CONFIGURATION.SHOW_CUSTOMER_PROTECTION
  },
  hasPreventiveActionsTool (state) {
    return state.PROBLEMS_CONFIGURATION.PREVENTIVE_ACTIONS_TOOL
  },
  hasProblemEvidences (state) {
    return state.PROBLEMS_CONFIGURATION.ENABLE_EVIDENCES_TAB
  },
  hasActionPlanTab (state) {
    return state.PROBLEMS_CONFIGURATION.ENABLE_ACTION_PLAN_TAB
  },
  hasAnalysisTab (state) {
    return state.PROBLEMS_CONFIGURATION.ENABLE_ANALYSIS_TAB
  },
  hasRootCauseTab (state) {
    return state.PROBLEMS_CONFIGURATION.ENABLE_ROOT_CAUSE_TAB
  },
  hasProblemIdentificationEnabled (state) {
    return state.PROBLEMS_CONFIGURATION.PROBLEM_IDENTIFICATION
  },
  LPAConfiguration (state) {
    return state.LPA_CONFIGURATION && Object.keys(state.LPA_CONFIGURATION || {}).length
  },
  hasEnableActionsSource (state) {
    return state.ENABLE_CUSTOM_ACTION_PLAN_SOURCES
  },
  permissionsMatrix (state) {
    return state.PERMISSIONS_MATRIX
  },
  scoreOptions (state) {
    return state.SCORE_OPTIONS
  },
  thresholds (state) {
    return state.THRESHOLDS
  },
  chartsVisibility (state) {
    return state.CHART_VISIBILITY
  },
  evolutionChartConfigs (state) {
    return state.EVOLUTION_CHARTS
  },
  variableNumberOfResultsChartsConfigs (state) {
    return state.VARIABLE_NUMBER_OF_RESULTS_CHARTS
  },
  chartsConfig (state) {
    return state.CHARTS_CONFIG
  },
  getInstalationType (state) {
    return state?.APP_INSTALLATION_TYPE
  },
  getSkillMatrix (state) {
    return state.SKILL_MATRIX
  },
  getTaskManager (state) {
    return state.TASK_MANAGER
  },
  getOkFirstPart (state) {
    return state.OK_FIRST_PART
  },
  hasEvidenceType (state) {
    return state.INCLUDE_EVIDENCE_TYPE
  },
  isSWREnabled (state) {
    return state[CONFIGURATION_KEYS.SWR_CONFIGURATION]['ENABLED'] ?? false
  },

  // Visual Standard Configuration

  hasTargetTime (state) {
    return state.STANDARD_CONFIGURATION.SHOW_TARGET_TIME
  },
  hasStandardViewer (state) {
    return state.STANDARD_CONFIGURATION.ACTIVE
  },
  includeSequenceEnumeration (state) {
    return state.STANDARD_CONFIGURATION.SHOW_ENUMERATION_SEQUENCES
  },
  includeSkillMatrix (state) {
    return state.SKILL_MATRIX.SHOW_SKILL_MATRIX
  },
  includeCTC (state) {
    return state.STANDARD_CONFIGURATION.SHOW_CTC
  },
  /**
   *
   * @param state
   * @returns {Boolean}
   */
  includeReferenceStandards (state) {
    return state.STANDARD_CONFIGURATION.REFERENCE_STANDARDS
  },
  includeRSRS (state) {
    return state.STANDARD_CONFIGURATION.SHOW_RSRS
  },
  sequenceRSRSOptions (state) {
    return state.STANDARD_CONFIGURATION.RSRS_OPTIONS
  },
  includeSequenceKeyCharacteristics (state) {
    return state.STANDARD_CONFIGURATION.SEQUENCE_KEY_CHARACTERISTICS.length > 0
  },
  sequenceKeyCharacteristicsConfiguration (state) {
    return state.STANDARD_CONFIGURATION.SEQUENCE_KEY_CHARACTERISTICS
  },
  includeAnomaliesAndProhibitions (state) {
    return state.STANDARD_CONFIGURATION.SHOW_ANOMALIES_AND_PROHIBITIONS
  },
  includePPEs (state) {
    return state.STANDARD_CONFIGURATION.SHOW_PPES
  },
  standardIncludeOperationNumberInSequence (state) {
    return state.STANDARD_CONFIGURATION.SET_OPERATION_NUMBER_IN_SEQUENCE
  },
  getAnnotationCategories (state) {
    return state.STANDARD_CONFIGURATION.ANNOTATION_CATEGORIES
  },
  getFieldsForAnnotationCategory: (state) => (categoryID) => {
    const category = state.STANDARD_CONFIGURATION.ANNOTATION_CATEGORIES.find(({ ID }) => ID === categoryID)
    if (!category) { return [] }
    return category.FIELDS
  },
  vsVideoPlayerBehaviour (state) {
    return state.STANDARD_CONFIGURATION.VIDEO_PLAYER_BEHAVIOUR
  },
  vsOnlyAdminCanDeleteStandards (state) {
    return state.STANDARD_CONFIGURATION.ONLY_ADMIN_CAN_DELETE_STANDARDS
  },
  vsStandardImprovementUtilsAreEnabled (state) {
    return state.STANDARD_CONFIGURATION.STANDARD_IMPROVEMENT_UTILS
  },
  getDailyRoundCheckpointsConfig (state) {
    return state.DAILY_ROUND_CHECKPOINTS_CONFIG
  },
  getGembaWalkCheckpointsConfig (state) {
    return state.GEMBA_WALK_CHECKPOINTS_CONFIG
  },
  arePrioritiesEnabledOnProblemSolving (state) {
    return state.PROBLEMS_CONFIGURATION.SHOW_PRIORITIES
  },
  getEfficiencyKpiAchiveRules (state) {
    return state.PROBLEMS_CONFIGURATION.EFFICIENCY_KPI_ACHIEVE_RULE_CHOICES
  },
  getMasterAuditAssessments (state) {
    return state.MASTER_AUDIT_ASSESSMENTS
  },
  getProductionHierarchyLevels () {
    return useProductionHierarchyStore().getProductionHierarchyLevels
  },
  getProductionHierarchySettingsLevels: () => (name) => {
    return useProductionHierarchyStore().getProductionHierarchyLevels.map(level => {
      return {
        settings: `${level.name}${PRODUCTION_HIERARCHY_SETTINGS_SUFFIX}`,
        name: level.name,
      }
    }).find(level => level.settings === name)
  },
  getProductionHierarchyLastLevel () {
    const levels = useProductionHierarchyStore().getProductionHierarchyLevels
    return levels.at(-1)
  },
  getAllowEditShifts (state) {
    return state.ALLOW_EDIT_SHIFTS
  },
  showInstallationLink (state) {
    return state?.APP_INSTALLATION_TYPE !== 0
  },
}

const mutations = {
  includeFiveS (state, value) {
    state.INCLUDE_FIVES = value
  },
  includeFiveSList (state, value) {
    state.INCLUDE_FIVES_LIST = value
  },
  includeFiveSPlan (state, value) {
    state.INCLUDE_FIVES_PLAN = value
  },
  includeFiveSCalendar (state, value) {
    state.INCLUDE_FIVES_CALENDAR_CALENDAR = value
  },
  includeActions (state, value) {
    state.INCLUDE_ACTIONS = value
  },
  includeProblemSolving (state, value) {
    state.INCLUDE_PROBLEM_SOLVING = value
  },
  modifyPermissionsMatrix (state, value) {
    state.PERMISSIONS_MATRIX = value
  },
  include5SFamilyChart (state, value) {
    state.SHOW_FIVES_FAMILY_CHART = value
  },
  includeEvidenceType (state, value) {
    state.INCLUDE_EVIDENCE_TYPE = value
  },
  setCustomerInfo (state, value) {
    state.CUSTOMER_INFO = value
  },
  setAppsHowToInstallLink (state, value) {
    state.APPS_HOW_INSTALL = value
  },
  setScoreOptions (state, value) {
    state.SCORE_OPTIONS = value
  },
  setIsVTAutonomous (state, value) {
    state.IS_VT_AUTONOMOUS = value
  },
  setWeeklyMeetingEnabled (state, value) {
    state.ENABLE_DAILY_ROUND_WEEKLY_MEETING = value
  },
  setCustomerProtectionFields (state, value) {
    state.INCLUDE_CUSTOMER_PROTECTION_FIELDS = value
  },
  setAnalysisToolsConfiguration (state, value) {
    state.ANALYSIS_TOOLS_CONFIGURATION = value
  },
  setHasBrandingLogo (state, value) {
    state.HAS_BRANDING_LOGO = value
  },
  setDiagnosisConfiguration (state, value) {
    state.DIAGNOSIS_CONFIGURATION = value
  },
  setHasCustomLoginBackground (state, value) {
    state.HAS_CUSTOM_LOGIN_BACKGROUND = value
  },
  setLPAConfiguration (state, value) {
    state.LPA_CONFIGURATION = value
  },
  setInstalationType (state, value) {
    state.APP_INSTALLATION_TYPE = value
  },
  setLoginType (state, value) {
    state.LOGIN_TYPE = value
  },
  setLoginOptions (state, value) {
    state.LOGIN_OPTIONS = value
  },
  setOrderColumnsRows (state, value) {
    state.ORDER_COLUMNS_ROWS = value
  },
  setThresholds (state, value = {}) {
    state.THRESHOLDS = value
  },
  setChartsVisibility (state, value = {}) {
    state.CHART_VISIBILITY = value
  },
  setEvolutionChartConfigs (state, value) {
    state.EVOLUTION_CHARTS = value
  },
  setVariableNumberOfResultsChartsConfigs (state, value) {
    state.VARIABLE_NUMBER_OF_RESULTS_CHARTS = value
  },
  setChartsConfig (state, value) {
    state.CHARTS_CONFIG = value
  },
  setFactoryTimezone (state, value) {
    core.setFactoryTimezone(value)
    state.FACTORY_TIME_ZONE = value
  },
  setAppNowVersion (state, value) {
    state.VERSION_MPS_NOW = value
  },
  setLDAPUsers (state, value) {
    state.GATHER_LDAP_USERS = value
  },
  setAllowEditShifts (state, value) {
    state.ALLOW_EDIT_SHIFTS = value
  },
  setOverviewWidgets (state, value) {
    state.OVERVIEW_WIDGETS = value
  },
  setProblemsConfiguration (state, value) {
    state.PROBLEMS_CONFIGURATION = value
  },
  setSAPUsers (state, value) {
    state.GATHER_SAP_USERS = value
  },
  setThresholdByKey (state, payload) {
    const updatedThresholds = _cloneDeep(state.THRESHOLDS)
    updatedThresholds[payload.key] = payload.value
    state.THRESHOLDS = updatedThresholds
  },
  setChartVisibilityByKey (state, payload) {
    const updatedThresholds = _cloneDeep(state.CHART_VISIBILITY)
    updatedThresholds[payload.key] = payload.value
    state.CHART_VISIBILITY = updatedThresholds
  },
  setEvolutionChartConfigByKey (state, { key, frequency, quantity }) {
    state.EVOLUTION_CHARTS[key] = { frequency, quantity }
  },
  setVariableNumberOfResultsChartsConfigByKey (state, { key, quantity }) {
    state.VARIABLE_NUMBER_OF_RESULTS_CHARTS[key] = { quantity }
  },
  setChartConfigByKey (state, { key, value }) {
    state.CHARTS_CONFIG[key] = value
  },
  setConfigurationKeys (state, value) {
    state.CONFIGURATION_KEYS = value
  },
  setConfigurationKeysByKey (state, payload) {
    const index = state.CONFIGURATION_KEYS.findIndex((key) => key.id === payload.id)
    if (index !== -1) {
      state.CONFIGURATION_KEYS[index] = payload
    } else {
      state.CONFIGURATION_KEYS.push(payload)
    }
  },
  setVersion (state, value) {
    state.VERSION = value
  },
  setSkillMatrix (state, value) {
    state.SKILL_MATRIX = value
  },
  setStandardConfiguration (state, value) {
    state.STANDARD_CONFIGURATION = value
  },
  setHSERiskConfiguration (state, value) {
    state.HSE_CONFIGURATION = value
  },
  setTaskManager (state, value) {
    state.TASK_MANAGER = value
  },
  setEnableActionsSource (state, value) {
    state.ENABLE_CUSTOM_ACTION_PLAN_SOURCES = value
  },
  setDailyRoundCheckpointsConfig (state, value) {
    state.DAILY_ROUND_CHECKPOINTS_CONFIG = value
  },
  setGembaWalkCheckpointsConfig (state, value) {
    state.GEMBA_WALK_CHECKPOINTS_CONFIG = value
  },
  setOkFirstPartConfig (state, value) {
    state.OK_FIRST_PART = value
  },
  setMasterAuditAssessments (state, value) {
    state.MASTER_AUDIT_ASSESSMENTS = value
  },
  setPasswordRegex (state, value) {
    state.PASSWORD_REGEX = new RegExp(value)
  },
  setMaxAuthorizationTokens (state, value) {
    state.MAX_AUTHORIZATION_TOKENS = value
  },
  setPMMIntegration (state, value) {
    state.ENABLE_PMM_INTEGRATION = value
  },
  setSWRConfiguration: (state, value) => {
    state[CONFIGURATION_KEYS.SWR_CONFIGURATION] = value
  },
}

const actions = {
  async fetchConfiguration ({ commit }) {
    const configuration = await getConfiguration()
    commit('includeActions', configuration.INCLUDE_ACTIONS)
    commit('includeEvidenceType', configuration.INCLUDE_EVIDENCE_TYPES)
    commit('includeProblemSolving', configuration.INCLUDE_PROBLEM_SOLVING)
    commit('modifyPermissionsMatrix', configuration.PERMISSIONS_MATRIX)
    commit('setChartsConfig', configuration.CHARTS_CONFIG)
    commit('setChartsVisibility', configuration.CHART_VISIBILITY)
    commit('setCustomerInfo', configuration.CUSTOMER_INFO)
    commit('setDailyRoundCheckpointsConfig', configuration.DAILY_ROUND_CHECKPOINTS)
    commit('setDiagnosisConfiguration', configuration.DIAGNOSIS_CONFIGURATION)
    commit('setEnableActionsSource', configuration.ENABLE_CUSTOM_ACTION_PLAN_SOURCES)
    commit('setAllowEditShifts', configuration.ALLOW_EDIT_SHIFTS)
    commit('setEvolutionChartConfigs', configuration.EVOLUTION_CHARTS)
    commit('setGembaWalkCheckpointsConfig', configuration.GEMBA_WALK_CHECKPOINTS)
    commit('setHSERiskConfiguration', configuration.HSE_CONFIGURATION)
    commit('setInstalationType', configuration.APP_INSTALLATION_TYPE)
    commit('setIsVTAutonomous', configuration.IS_VT_AUTONOMOUS)
    commit('setLDAPUsers', configuration.GATHER_LDAP_USERS)
    commit('setLPAConfiguration', configuration.LPA_CONFIGURATION)
    commit('setMasterAuditAssessments', configuration.MASTER_AUDIT_ASSESSMENTS)
    commit('setOkFirstPartConfig', configuration.OK_FIRST_PART)
    commit('setOverviewWidgets', configuration.OVERVIEW_WIDGETS)
    commit('setProblemsConfiguration', configuration.PROBLEMS_CONFIGURATION)
    commit('setSAPUsers', configuration.GATHER_SAP_USERS)
    commit('setScoreOptions', configuration.SCORE_OPTIONS)
    commit('setSkillMatrix', configuration.SKILL_MATRIX)
    commit('setStandardConfiguration', configuration.STANDARD_CONFIGURATION)
    commit('setTaskManager', configuration.TASK_MANAGER)
    commit('setThresholds', configuration.THRESHOLDS)
    commit('setWeeklyMeetingEnabled', configuration.ENABLE_DAILY_ROUND_WEEKLY_MEETING)
    commit('setPasswordRegex', configuration.PASSWORD_REGEX)
    commit('setMaxAuthorizationTokens', configuration.MAX_AUTHORIZATION_TOKENS)
    commit('setPMMIntegration', configuration.ENABLE_PMM_INTEGRATION)
    commit('setVariableNumberOfResultsChartsConfigs', configuration.VARIABLE_NUMBER_OF_RESULTS_CHARTS)
    commit('setCustomerProtectionFields', configuration.CUSTOMER_PROTECTION_CONFIGURATION_FIELDS)
    commit('setAnalysisToolsConfiguration', configuration.ANALYSIS_TOOLS_CONFIGURATION)
    commit('setHasBrandingLogo', configuration.HAS_BRANDING_LOGO)
    commit('setSWRConfiguration', configuration.SWR_CONFIGURATION)

    useProductionHierarchyStore().setProductionHierarchyLevels(configuration.PRODUCTION_HIERARCHY_LEVELS)

    const customerConfigurationStore = useCustomerConfiguration()
    customerConfigurationStore.setCustomerInfo(configuration.CUSTOMER_INFO)

    if (configuration.APPS_HOW_INSTALL) commit('setAppsHowToInstallLink', configuration.APPS_HOW_INSTALL)
    if (configuration.ORDER_COLUMNS_ROWS) commit('setOrderColumnsRows', configuration.ORDER_COLUMNS_ROWS)

    const licenceStore = useLicenceExpirationConfiguration()
    licenceStore.licenceExpirationDateString = configuration.LICENCE_EXPIRATION_DATE

    const { fetchExperimentalFeatures } = useExperimentalFeaturesStore()
    await fetchExperimentalFeatures()

    return configuration
  },
  async createConfigurationKey ({ commit }, payload) {
    const response = await createConfigurationKey(payload)
    commit('setConfigurationKeysByKey', response)
    return response
  },
  async updateConfigurationKeysByKey ({ commit }, { key, value }) {
    const response = await updateConfigurationKey(key, { value })
    commit('setConfigurationKeysByKey', response)
    await useDeviceConfigurationStore().loadDeviceConfiguration()
    return response
  },
  updateThreshold ({ commit }, { key, value, mode }) {
    return getConfigurationByKeyName(CONFIGURATION_KEYS.THRESHOLDS_CONFIG)
      .then(remoteConfigurations => {
        const data = remoteConfigurations[0].value
        data[key] = {
          mode: mode || data[key].mode,
          value: value,
        }
        return updateConfigurationKey(remoteConfigurations[0].id, { value: data })
          .then(() => {
            commit('setThresholdByKey', { key, value: { mode: data[key].mode, value: value } })
          })
      })
  },
  updateConfigByConfigurationKeyNameAndPath ({ commit }, { key, path, value }) {
    return getConfigurationByKeyName(CONFIGURATION_KEYS.AUDITS_ACTION_EVIDENCES)
      .then(remoteConfigurations => {
        const fullValue = remoteConfigurations[0].value
        _set(fullValue, path, value)
        return updateConfigurationKey(remoteConfigurations[0].id, { key, value: fullValue })
          .then(() => {
            commit('setConfigurationKeysByKey', remoteConfigurations[0])
          })
      })
  },
  updateChartVisibility ({ commit }, payload) {
    return getConfigurationByKeyName(CONFIGURATION_KEYS.CHART_VISIBILITY_CONFIG)
      .then(remoteConfigurations => {
        const data = remoteConfigurations[0].value
        data[payload.key] = payload.value
        return updateConfigurationKey(remoteConfigurations[0].id, { value: data })
          .then(() => {
            commit('setChartVisibilityByKey', payload)
          })
      })
  },
  updateEvolutionChartConfig ({ commit }, { key, frequency, quantity }) {
    return getConfigurationByKeyName(CONFIGURATION_KEYS.EVOLUTION_CHARTS_CONFIG)
      .then(remoteConfigurations => {
        const data = remoteConfigurations[0].value
        data[key] = { frequency, quantity }
        return updateConfigurationKey(remoteConfigurations[0].id, { value: data })
          .then(() => {
            commit('setEvolutionChartConfigByKey', { key, frequency, quantity })
          })
      })
  },
  updateVariableNumberOfResultsChartsConfig ({ commit }, { key, quantity }) {
    return getConfigurationByKeyName(CONFIGURATION_KEYS.VARIABLE_NUMBER_OF_RESULTS_CHARTS_CONFIG)
      .then(remoteConfigurations => {
        const data = remoteConfigurations[0].value
        data[key] = { quantity }
        return updateConfigurationKey(remoteConfigurations[0].id, { value: data })
          .then(() => {
            commit('setVariableNumberOfResultsChartsConfigByKey', { key, quantity })
          })
      })
  },
  updateChartConfig ({ commit }, payload) {
    return getConfigurationByKeyName(CONFIGURATION_KEYS.CHARTS_CONFIG)
      .then(remoteConfigurations => {
        const data = remoteConfigurations[0].value
        data[payload.key] = payload.value
        return updateConfigurationKey(remoteConfigurations[0].id, { value: data })
          .then(() => {
            commit('setChartConfigByKey', payload)
          })
      })
  },
  async updateSessionTime ({ commit }, payload) {
    const config = await getConfigurationByKeyName(CONFIGURATION_KEYS.SYSTEM_CONFIGURATION)
    const response = await updateConfigurationKey(config[0].id, { value: payload })
    commit('setConfigurationKeysByKey', response)
  },
  async fetchConfigurationsKeys ({ commit }) {
    const response = await getConfigurationKeys()
    const configurations_sorted = _orderBy(response, ['id'], ['asc'])
    commit('setConfigurationKeys', configurations_sorted)
  },
  setVsOnlyAdminCanDeleteStandards ({ state, getters, dispatch }, value) {
    const configKey = getters.getConfigurationKeyByName(CONFIGURATION_KEYS.STANDARD_CONFIGURATION_ONLY_ADMIN_CAN_DELETE_STANDARDS).id
    return dispatch('updateConfigurationKeysByKey', {
      key: configKey,
      value,
    }).then(() => {
      state.STANDARD_CONFIGURATION.ONLY_ADMIN_CAN_DELETE_STANDARDS = value
    })
  },
  setVsVideoPlayerBehaviour ({ state, getters, dispatch }, value) {
    const configKey = getters.getConfigurationKeyByName(CONFIGURATION_KEYS.STANDARD_CONFIGURATION_VIDEO_PLAYER_BEHAVIOUR).id
    return dispatch('updateConfigurationKeysByKey', {
      key: configKey,
      value,
    }).then(() => {
      state.STANDARD_CONFIGURATION.VIDEO_PLAYER_BEHAVIOUR = value
    })
  },
}

export default {
  actions,
  getters: gettersObj,
  mutations,
  state: stateObj,
}
